import React, { FC, useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import { palette } from '../../theme';
import { parseRaceDateTime } from '../../helpers/general.helpers';
import type { Race, RaceLogEntry, CreateTeamRaceLogsPayload } from '../../models/race.models';
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import TeamsService from '../../services/teams.service';

interface TeamMember {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface CreateTeamRaceLogsModalProps {
  open: boolean;
  onClose: () => void;
  race: Race | null;
  races?: Race[];
  onRaceChange?: (race: Race | null) => void;
  /** Existing race logs for the selected race — members with logs are hidden from the list */
  existingEntries?: RaceLogEntry[];
  teamId: string;
  onSubmit: (payload: CreateTeamRaceLogsPayload) => Promise<void>;
}

const CreateTeamRaceLogsModal: FC<CreateTeamRaceLogsModalProps> = ({
  open,
  onClose,
  race,
  races,
  onRaceChange,
  existingEntries = [],
  teamId,
  onSubmit,
}) => {
  const [raceType, setRaceType] = useState('');
  const [results, setResults] = useState<Record<string, number>>({});
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const teamsService = useService(TeamsService);
  const { showSnackbar } = useSnackbarHelper();
  const hasFetchedForOpenRef = useRef(false);

  const showRacePicker = Boolean(races && races.length > 0 && onRaceChange);
  const { dateDisplay, timeDisplay } = parseRaceDateTime(race?.date ?? '');

  const existingMemberIds = useMemo(() => {
    const ids = new Set<string>();
    const names = new Set<string>();
    existingEntries.forEach((entry) => {
      if (entry.member_id != null) ids.add(String(entry.member_id));
      if (entry.athlete_name) names.add(entry.athlete_name.trim().toLowerCase());
    });
    return { ids, names };
  }, [existingEntries]);

  const availableMembers = useMemo(() => {
    return members.filter((member) => {
      if (existingMemberIds.ids.has(String(member.id))) return false;
      const name = (member.name ?? '').trim().toLowerCase();
      if (name && existingMemberIds.names.has(name)) return false;
      return true;
    });
  }, [members, existingMemberIds]);

  useEffect(() => {
    if (!open) {
      hasFetchedForOpenRef.current = false;
      return;
    }
    if (hasFetchedForOpenRef.current) return;
    hasFetchedForOpenRef.current = true;

    setRaceType('');
    setResults({});
    setMembersLoading(true);
    teamsService
      .getAllTeamMembers(teamId)
      .then((response: { data: { data: TeamMember[] } }) => {
        setMembers(response.data?.data ?? []);
      })
      .catch(() => {
        showSnackbar('Failed to load team members.', 'error');
        setMembers([]);
      })
      .finally(() => {
        setMembersLoading(false);
      });
  }, [open, teamId, teamsService, showSnackbar]);

  // Drop any result values for members who already have a log when entries/race change
  useEffect(() => {
    setResults((prev) => {
      const next: Record<string, number> = {};
      Object.entries(prev).forEach(([memberId, value]) => {
        if (!existingMemberIds.ids.has(memberId)) {
          next[memberId] = value;
        }
      });
      return next;
    });
  }, [existingMemberIds, race?.id]);

  const handleResultChange = (memberId: string, value: string) => {
    const num = value === '' ? NaN : parseInt(value, 10);
    setResults((prev) => {
      const next = { ...prev };
      if (Number.isFinite(num)) {
        next[memberId] = num;
      } else {
        delete next[memberId];
      }
      return next;
    });
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = async () => {
    if (!race) {
      showSnackbar('Please select a race.', 'warning');
      return;
    }
    const trimmedRaceType = raceType.trim();
    if (!trimmedRaceType) {
      showSnackbar('Please enter race type.', 'warning');
      return;
    }
    const resultEntries = Object.entries(results).filter(([, v]) => Number.isFinite(v));
    if (resultEntries.length === 0) {
      showSnackbar('Please enter at least one result.', 'warning');
      return;
    }
    const payload: CreateTeamRaceLogsPayload = {
      race_type: trimmedRaceType,
      results: Object.fromEntries(resultEntries) as Record<string, number>,
    };
    setSubmitting(true);
    try {
      await onSubmit(payload);
      showSnackbar('Race logs added successfully.', 'success');
      onClose();
    } catch {
      showSnackbar('Failed to add race logs.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resultCount = Object.keys(results).filter((k) => Number.isFinite(results[k])).length;
  const canSubmit = Boolean(race) && raceType.trim().length > 0 && resultCount > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>Add Race Log</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          {showRacePicker && (
            <FormControl size="small" fullWidth>
              <InputLabel id="add-race-select-label">Race</InputLabel>
              <Select
                labelId="add-race-select-label"
                label="Race"
                value={race?.id ?? ''}
                onChange={(e) => {
                  const next = races?.find((r) => r.id === Number(e.target.value)) ?? null;
                  onRaceChange?.(next);
                }}
              >
                {races?.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {race && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                gap: 2,
                p: 1.5,
                backgroundColor: palette.background.subtle,
                borderRadius: 2,
                border: `1px solid ${palette.grey[200]}`,
              }}
            >
              <Box>
                <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
                  Race name
                </Typography>
                <Typography variant="subtitle2">{race.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
                  Date
                </Typography>
                <Typography variant="subtitle2">{dateDisplay || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
                  Time
                </Typography>
                <Typography variant="subtitle2">{timeDisplay || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
                  Location
                </Typography>
                <Typography variant="subtitle2">{race.location || '—'}</Typography>
              </Box>
            </Box>
          )}

          <TextField
            label="Race type"
            value={raceType}
            onChange={(e) => setRaceType(e.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            placeholder="e.g. 10 km Classic"
          />

          <Typography variant="subtitle2" sx={{ color: palette.text.secondary, mt: 0.5 }}>
            Athlete results
          </Typography>
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List
              dense
              sx={{ border: `1px solid ${palette.grey[200]}`, borderRadius: 2, maxHeight: 280, overflow: 'auto' }}
            >
              {availableMembers.map((member) => (
                <ListItem
                  key={member.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${palette.grey[100]}` }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {member.name ?? member.id}
                  </Typography>
                  <TextField
                    type="number"
                    label="Result"
                    size="small"
                    value={results[member.id] ?? ''}
                    onChange={(e) => handleResultChange(member.id, e.target.value)}
                    inputProps={{ min: 1 }}
                    sx={{ width: 100 }}
                  />
                </ListItem>
              ))}
              {availableMembers.length === 0 && !membersLoading && (
                <ListItem>
                  <Typography variant="body2" color="text.secondary">
                    {members.length === 0
                      ? 'No team members found.'
                      : 'All team members already have race logs for this race.'}
                  </Typography>
                </ListItem>
              )}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting || membersLoading || availableMembers.length === 0}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTeamRaceLogsModal;
